/**
 * Fase 2 Sub-release 2.4b — Modale primo accesso cliente per raccogliere le preferenze
 * di calcolo (Durata + Km/anno) e calibrare le rate mostrate nel catalogo.
 *
 * Uso tipico:
 *   <PreferenzeQuoteDialog
 *     open={showPrefs}
 *     onClose={() => setShowPrefs(false)}
 *     onSaved={(prefs) => {...}}
 *   />
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { CalendarClock, Gauge, Sparkles, Loader2, Wallet, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 2.6.14 — Rimossa durata 24 mesi (non presente nei listini Excel)
const DURATE = [36, 48, 60];
const KM_OPTIONS = [10000, 15000, 20000, 25000];
const FASCE_BUDGET = [
  { value: 'lt_200', label: 'Fino a 200€', min: 0, max: 200 },
  { value: '201_400', label: '201€ - 400€', min: 201, max: 400 },
  { value: '401_600', label: '401€ - 600€', min: 401, max: 600 },
  { value: 'gt_600', label: 'Oltre 600€', min: 601, max: 99999 },
];

export default function PreferenzeQuoteDialog({ open, onClose, onSaved, initialDurata = 48, initialKm = 15000, initialFasciaBudget = null, initialAbilita4Vantage = null }) {
  const { getAuthHeader } = useAuth();
  const [durata, setDurata] = useState(initialDurata);
  const [km, setKm] = useState(initialKm);
  const [fasciaBudget, setFasciaBudget] = useState(initialFasciaBudget);
  // 2.6.14b — Domanda secca "Sei un dipendente o una P.IVA forfettaria?" Sì/No.
  // null = non risposto (blocca salvataggio), true = mostra 4Vantage, false = nasconde.
  const [abilita4Vantage, setAbilita4Vantage] = useState(initialAbilita4Vantage);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      // 2.6.14 — Normalizza eventuali valori legacy (24 mesi → 36)
      setDurata((initialDurata && [36, 48, 60].includes(initialDurata)) ? initialDurata : 48);
      setKm(initialKm || 15000);
      setFasciaBudget(initialFasciaBudget || null);
      setAbilita4Vantage(typeof initialAbilita4Vantage === 'boolean' ? initialAbilita4Vantage : null);
    }
  }, [open, initialDurata, initialKm, initialFasciaBudget, initialAbilita4Vantage]);

  const handleSave = async () => {
    if (!fasciaBudget) {
      toast.error('Seleziona una fascia di canone desiderata');
      return;
    }
    if (typeof abilita4Vantage !== 'boolean') {
      toast.error('Rispondi alla domanda "Sei un dipendente o una P.IVA forfettaria?"');
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/client/preferenze-quote`,
        { durata, km, fascia_budget: fasciaBudget, abilita_4vantage: abilita4Vantage },
        { headers: getAuthHeader() },
      );
      toast.success('Preferenze salvate: il catalogo è ora calibrato su di te.');
      if (typeof onSaved === 'function') onSaved({ durata, km, fascia_budget: fasciaBudget, abilita_4vantage: abilita4Vantage });
      if (typeof onClose === 'function') onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio delle preferenze');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && typeof onClose === 'function') onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="preferenze-quote-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <DialogTitle>Personalizza il tuo catalogo</DialogTitle>
              <DialogDescription>
                Scegli durata e km/anno per vedere subito le rate su misura per te.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {/* Durata */}
          <div>
            <Label className="text-sm font-semibold flex items-center gap-1.5 mb-2">
              <CalendarClock className="w-4 h-4 text-slate-500" />
              Durata contratto
            </Label>
            <div className="grid grid-cols-3 gap-2">
              {DURATE.map((d) => (
                <button
                  type="button"
                  key={d}
                  onClick={() => setDurata(d)}
                  data-testid={`pref-durata-${d}`}
                  className={`px-2 py-2 rounded-lg text-sm font-semibold border transition
                    ${durata === d
                      ? 'bg-emerald-600 text-white border-emerald-600 shadow'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-300'}`}
                >
                  {d} mesi
                </button>
              ))}
            </div>
          </div>

          {/* Km */}
          <div>
            <Label className="text-sm font-semibold flex items-center gap-1.5 mb-2">
              <Gauge className="w-4 h-4 text-slate-500" />
              Km percorsi in un anno
            </Label>
            <div className="grid grid-cols-2 gap-2">
              {KM_OPTIONS.map((k) => (
                <button
                  type="button"
                  key={k}
                  onClick={() => setKm(k)}
                  data-testid={`pref-km-${k}`}
                  className={`px-3 py-2 rounded-lg text-sm font-semibold border transition
                    ${km === k
                      ? 'bg-emerald-600 text-white border-emerald-600 shadow'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-300'}`}
                >
                  {k.toLocaleString('it-IT')} km
                </button>
              ))}
            </div>
          </div>

          {/* Fascia di canone desiderata */}
          <div>
            <Label className="text-sm font-semibold flex items-center gap-1.5 mb-2">
              <Wallet className="w-4 h-4 text-slate-500" />
              Fascia di canone desiderata
            </Label>
            <div className="grid grid-cols-2 gap-2">
              {FASCE_BUDGET.map((f) => (
                <button
                  type="button"
                  key={f.value}
                  onClick={() => setFasciaBudget(f.value)}
                  data-testid={`pref-budget-${f.value}`}
                  className={`px-3 py-2 rounded-lg text-sm font-semibold border transition
                    ${fasciaBudget === f.value
                      ? 'bg-emerald-600 text-white border-emerald-600 shadow'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-300'}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* 2.6.14b — Domanda secca 4Vantage: Sì/No */}
          <div>
            <Label className="text-sm font-semibold flex items-center gap-1.5 mb-2">
              <HelpCircle className="w-4 h-4 text-slate-500" />
              Sei un dipendente o una P.IVA forfettaria?
            </Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setAbilita4Vantage(true)}
                data-testid="pref-4vantage-si"
                className={`px-3 py-2 rounded-lg border text-sm font-semibold transition
                  ${abilita4Vantage === true
                    ? 'bg-emerald-600 text-white border-emerald-600 shadow'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-300'}`}
              >
                Sì
              </button>
              <button
                type="button"
                onClick={() => setAbilita4Vantage(false)}
                data-testid="pref-4vantage-no"
                className={`px-3 py-2 rounded-lg border text-sm font-semibold transition
                  ${abilita4Vantage === false
                    ? 'bg-slate-700 text-white border-slate-700 shadow'
                    : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}
              >
                No
              </button>
            </div>
          </div>

          <div className="pt-2 border-t border-slate-100 flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              className="flex-1"
              disabled={saving}
              onClick={() => { if (typeof onClose === 'function') onClose(); }}
              data-testid="btn-pref-skip"
            >
              Salta per ora
            </Button>
            <Button
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              onClick={handleSave}
              disabled={saving}
              data-testid="btn-pref-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salva e continua'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
